import { useAsyncJobsContext } from "@kaltura/mediaspace-shared-contexts/AsyncJobsContext";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { ApproveReject } from "../ApproveReject";
import { useState } from "react";
import { translatePlural } from "@kaltura/ds-react-utils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import ErrorMessage from "../ErrorMessage";
import { Breakpoint } from "@mui/material";

interface RejectResponse {
    success: boolean;
    errors?: { id: string; name: string }[];
}

export interface RejectContainerProps {
    /**
     * callback for letting the app know media list should be refreshed
     */
    reloadMedia: () => void;

    /**
     * reject action url
     * ie kms-base-url/channelmoderation/index/reject-entries
     */
    url: string;

    /**
     * ids of current entries
     */
    entryIds: string[];

    /**
     * name of current entry
     */
    entryName: string;

    /**
     * if confirmation required before rejection
     */
    requestConfirmation: boolean;

    /**
     * Keep the button large even if the button text is hidden
     * (according to the noTextBreakpoint prop).
     */
    forceLargeButton?: boolean;

    /**
     * The button will have no visible text under this breakpoint (default: "md").
     * Specify "xs" to never hide the text.
     */
    noTextBreakpoint?: Breakpoint;

    /**
     * threshold for creating bulk async actions for approving/rejecting media
     */
    bulkThreshold?: number;

    /**
     * analytics data
     */
    analytics: {
        action: string;
        buttonType: ButtonClickAnalyticsType;
    };

    /**
     * if the button is disabled
     */
    disabled?: boolean;
}

export function RejectContainer(props: RejectContainerProps) {
    const { reloadMedia, url, entryIds, requestConfirmation, analytics, bulkThreshold, ...rest } = props;
    const [loading, setLoading] = useState(false);
    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();
    const { startPolling } = useAsyncJobsContext();
    const isBulkAsync = !!bulkThreshold && entryIds.length > bulkThreshold;

    let confirmationMessage = "";
    if (requestConfirmation) {
        confirmationMessage =
            entryIds.length > 1
                ? translate("Are you sure you want to reject %1 media?", [entryIds.length])
                : translate("Are you sure you want to reject “%1” ?", [props.entryName]);
    }
    if (isBulkAsync) {
        if (confirmationMessage) {
            confirmationMessage += "<br>";
        }
        confirmationMessage
            += translate(
                "This action may take a few minutes. During this time, you won’t be able to make additional changes to the media on this page. Do you wish to continue?"
            );
    }

    const sendAnalytics = (what: "act" | "act_confirm") => {
        switch (what) {
            case "act":
                // main action button
                sendButtonAnalytics(
                    analytics.action,
                    analytics.buttonType,
                    entryIds.length === 1 ? entryIds[0] : undefined
                );
                break;
            case "act_confirm":
                // action from confirmation
                sendButtonAnalytics(
                    "Confirmation box - Reject media",
                    analytics.buttonType,
                    entryIds.length === 1 ? entryIds[0] : undefined
                );
                break;
        }
    };

    /**
     * initiate a call to reject media and handle result
     */
    const reject = () => {
        setLoading(true);
        postKmsData(
            url,
            {
                entryIds: entryIds,
            }
        )
            .then(
                (response: RejectResponse) => {
                    const itemsCount = entryIds.length;
                    if (response.success) {
                        if (isBulkAsync) {
                            // async job started: no toast, start polling
                            startPolling();
                        }
                        else {
                            // if not async, show toast
                            const messageText = translatePlural(
                                "Media successfully rejected",
                                "%1 Media successfully rejected",
                                [itemsCount]
                            );
                            showToast({ severity: "success", message: messageText });
                        }
                    }
                    else if (response.errors) {
                        showToast({
                            severity: "error",
                            message: (
                                <ErrorMessage
                                    titleText={translate("Reject failed for the following items:")}
                                    errors={response.errors}
                                />
                            ),
                        });
                    }
                    else {
                        showToast({ severity: "error", message: translate("Some media could not be rejected") });
                    }
                },
                (error) => {
                    showToast({ severity: "error", message: translate("Some media could not be rejected") });
                }
            )
            .finally(() => {
                setLoading(false);
                // notify
                reloadMedia();
            });
    };

    return (
        <ApproveReject
            {...rest}
            action={"reject"}
            onClick={reject}
            loading={loading}
            requestConfirmation={requestConfirmation}
            confirmationMessage={confirmationMessage}
            isBulkAsync={isBulkAsync}
            report={sendAnalytics}
        />
    );
}

export default RejectContainer;
